'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { getToneColor } from '../../lib/tone-utils'
import { useChineseFont } from '@/lib/chinese-font'
import { cn } from '@/lib/utils'

interface Props {
  text: string
  tones: number[]
  size?: 'sm' | 'md' | 'lg' | 'xl'
  scriptType?: 'simplified' | 'traditional'
  className?: string
  instanceId?: string // Unique identifier for this SelectableText instance (e.g., "0-0-0" for paragraph-sentence-line)
  onSelect?: (
    selectedText: string,
    startIndex: number,
    endIndex: number,
    scriptType?: 'simplified' | 'traditional'
  ) => void
  selectedText?: string // External control to clear selection
  selectedIndices?: { start: number; end: number } // External control for exact position
  onCopy?: () => void
}

export function SelectableText({
  text,
  tones,
  size = 'md',
  scriptType,
  className,
  instanceId,
  onSelect,
  selectedText,
  selectedIndices,
  onCopy
}: Props) {
  // Only track selection while dragging.
  // Once drag ends, we fire onSelect and rely on props (selectedIndices) for highlighting.
  const [dragSelection, setDragSelection] = useState<{
    start: number
    end: number
  } | null>(null)

  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isStartingSelectionRef = useRef(false)
  const chineseFont = useChineseFont()

  const chars = text.split('')
  const sizeClasses = {
    sm: 'text-base',
    md: 'text-lg',
    lg: 'text-2xl font-medium',
    xl: 'text-6xl font-medium'
  }

  const getCharIndexFromEvent = useCallback(
    (e: MouseEvent): number | null => {
      if (!containerRef.current) return null
      const rect = containerRef.current.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      // Use a more specific selector if instanceId is provided
      const selector = instanceId
        ? `span[data-char-index][data-instance-id="${instanceId}"]`
        : 'span[data-char-index]'

      const spans = containerRef.current.querySelectorAll(selector)

      // Find the span that contains the click point, preferring exact matches
      let bestMatch: { span: Element; distance: number } | null = null

      for (const span of spans) {
        const spanRect = span.getBoundingClientRect()
        const left = spanRect.left - rect.left
        const right = spanRect.right - rect.left
        const top = spanRect.top - rect.top
        const bottom = spanRect.bottom - rect.top

        // Check if click is within this span's bounds
        if (x >= left && x <= right && y >= top && y <= bottom) {
          // Calculate distance from click to center of span
          const centerX = (left + right) / 2
          const centerY = (top + bottom) / 2
          const distance = Math.sqrt(
            Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
          )

          // Keep the closest match
          if (!bestMatch || distance < bestMatch.distance) {
            bestMatch = { span, distance }
          }
        }
      }

      if (bestMatch) {
        const indexStr = bestMatch.span.getAttribute('data-char-index') || '0'
        // If using composite IDs, extract just the character index (last part after last dash)
        const index = instanceId
          ? parseInt(indexStr.split('-').pop() || '0')
          : parseInt(indexStr)
        return index
      }

      return null
    },
    [instanceId]
  )

  const normalizeSelection = (sel: { start: number; end: number }) => ({
    start: Math.min(sel.start, sel.end),
    end: Math.max(sel.start, sel.end)
  })

  const handleCharMouseDown = (index: number, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    isStartingSelectionRef.current = true
    setTimeout(() => (isStartingSelectionRef.current = false), 100)

    // Start a new drag selection
    setDragSelection({ start: index, end: index })
    setIsDragging(true)

    // Prevent the event from bubbling to Popover's click handlers
    e.nativeEvent.stopImmediatePropagation()
  }

  const handleCharMouseEnter = (index: number) => {
    if (isDragging && dragSelection) {
      setDragSelection({ start: dragSelection.start, end: index })
    }
  }

  useEffect(() => {
    if (!isDragging || !dragSelection) return

    const handleMouseMove = (e: MouseEvent) => {
      const index = getCharIndexFromEvent(e)
      if (index !== null && index !== dragSelection.end) {
        setDragSelection({ start: dragSelection.start, end: index })
      }
    }

    const handleMouseUp = (e: MouseEvent) => {
      // Prevent event from bubbling to avoid conflicts with Popover
      e.stopPropagation()

      if (onSelect && dragSelection) {
        const { start, end } = normalizeSelection(dragSelection)
        const newSelectedText = chars.slice(start, end + 1).join('')
        if (newSelectedText) {
          // Use setTimeout to defer onSelect call, allowing state updates to complete first
          // This prevents the Popover from closing immediately
          setTimeout(() => {
            onSelect(newSelectedText, start, end, scriptType)
          }, 0)
        }
      }
      // Clear dragging state immediately
      setIsDragging(false)
      setDragSelection(null)
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    window.addEventListener('mouseup', handleMouseUp, { capture: true })
    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp, { capture: true })
    }
  }, [
    isDragging,
    dragSelection,
    onSelect,
    chars,
    scriptType,
    getCharIndexFromEvent
  ])

  // Keyboard shortcut for copying selected text
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isCopyShortcut =
        (e.metaKey || e.ctrlKey) && e.key === 'c' && !e.shiftKey && !e.altKey

      if (!isCopyShortcut) return

      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      // Use props for current selection
      if (!selectedText) return

      if (containerRef.current) {
        const activeElement = document.activeElement
        const isContainerFocused =
          activeElement === containerRef.current ||
          containerRef.current.contains(activeElement) ||
          activeElement?.closest('[data-slot="popover-content"]')

        const browserSelection = window.getSelection()
        if (
          browserSelection &&
          browserSelection.toString().trim() &&
          !isContainerFocused
        ) {
          return
        }
      }

      navigator.clipboard
        .writeText(selectedText)
        .then(() => {
          onCopy?.()
        })
        .catch((err) => console.error('Failed to copy:', err))

      e.preventDefault()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [selectedText, onCopy])

  // Determine what to highlight: drag selection (if active) or prop selection
  const activeRange =
    isDragging && dragSelection
      ? normalizeSelection(dragSelection)
      : selectedIndices
        ? normalizeSelection(selectedIndices)
        : null

  return (
    <div
      ref={containerRef}
      className={cn(
        'flex cursor-text select-none',
        sizeClasses[size],
        chineseFont,
        className
      )}
      style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
    >
      {chars.map((char, i) => {
        const isSelected =
          activeRange && i >= activeRange.start && i <= activeRange.end

        // Create composite ID: if instanceId is provided, use "instanceId-charIndex"
        // Otherwise, just use the character index
        const charId = instanceId ? `${instanceId}-${i}` : String(i)

        return (
          <span
            key={charId}
            data-char-index={charId}
            data-instance-id={instanceId}
            className={cn(
              getToneColor(tones[i] || 5),
              'cursor-text',
              isSelected && 'bg-blue-400/20'
            )}
            onMouseDown={(e) => handleCharMouseDown(i, e)}
            onMouseEnter={() => handleCharMouseEnter(i)}
          >
            {char}
          </span>
        )
      })}
    </div>
  )
}
